import { useEffect, useState } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, Lock, X } from "lucide-react";

// Finalise a shipment's bill (amount incl. GST + chargeable weight) after reconciliation, optionally raise the invoice.
// Once finalised, reconciliation can only propose; an admin can reopen.
const fmt = (n) => (n === null || n === undefined || n === "" ? "-" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const inp = "w-full h-9 rounded-md border border-slate-300 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400";

export default function FinaliseBillDialog({ shipment, isAdmin, onClose, onDone }) {
  const [p, setP] = useState(null);
  const [amount, setAmount] = useState("");
  const [weight, setWeight] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/v2/billing/shipments/${shipment.id}/final`);
      setP(r.data);
      setAmount(String(r.data.final_locked ? r.data.current_bill : r.data.suggested_amount ?? ""));
      setWeight(String(r.data.suggested_weight ?? ""));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load the bill"); onClose(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [shipment.id]);

  const differs = p && Math.abs(Number(amount) - Number(p.current_bill)) > 0.5;
  const ok = p && !p.final_locked && Number(amount) > 0 && Number(weight) > 0 && (!differs || reason.trim());

  const finalise = async (raise) => {
    setBusy(true);
    try {
      const r = await api.post(`/v2/billing/shipments/${shipment.id}/finalise`,
        { final_amount: Number(amount), final_weight: Number(weight), reason: reason.trim(), raise_invoice: raise });
      toast.success(`Final bill ${fmt(r.data.final_amount)} locked` + (r.data.invoice?.invoice_number ? ` · invoice ${r.data.invoice.invoice_number}` : ""));
      (r.data.notes || []).forEach((n) => toast.message(n));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not finalise"); }
    finally { setBusy(false); }
  };
  const raise = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/v2/billing/shipments/${shipment.id}/raise-invoice`);
      toast.success(`Invoice ${r.data.invoice_number || ""} ${fmt(r.data.total_amount)}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not raise the invoice"); }
    finally { setBusy(false); }
  };
  const reopen = async () => {
    const why = window.prompt("Reason for reopening this final bill?");
    if (!why || !why.trim()) return;
    setBusy(true);
    try { await api.post(`/v2/billing/shipments/${shipment.id}/reopen`, { reason: why.trim() }); toast.success("Reopened"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reopen"); }
    finally { setBusy(false); }
  };

  const Row = ({ k, v, hint }) => (
    <div className="flex justify-between gap-3 py-1 text-sm border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{k}</span><span className="font-semibold text-slate-800 text-right">{v}{hint && <span className="block text-[11px] font-normal text-slate-400">{hint}</span>}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="finalise-bill-dialog">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-bold text-lg text-slate-800">Final bill — {shipment.order_id}</h3>
          {p?.final_locked && <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700"><Lock className="w-3 h-3" />Finalised</span>}
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!p ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (<>
          <div className="rounded-lg border border-slate-200 px-3 py-2 mb-4">
            <Row k="Booked amount" v={fmt(p.booked_amount)} />
            <Row k="Current bill" v={fmt(p.current_bill)} />
            {p.reconciled_bill && <Row k="After Delhivery reconciliation" v={fmt(p.reconciled_bill)} hint={`Delhivery ${fmt(p.carrier_total)} + ${p.margin_percent}% margin`} />}
            {p.recon_proposal && <Row k="New reconciliation proposal" v={fmt(p.recon_proposal.final_amount)} hint="arrived after finalising - review" />}
            <Row k="Weight booked / now / reconciled" v={`${p.booked_weight ?? "-"} / ${p.current_weight ?? "-"} / ${p.reconciled_weight ?? "-"} kg`} />
            {p.wallet?.wallet_flow && <Row k="Taken from wallet for this shipment" v={fmt(p.wallet.net_charged)} hint="settled to the final bill when you finalise" />}
            {Number(p.credit_amount) > 0 && <Row k="Owed on credit" v={fmt(p.credit_amount)} hint={p.credit_settled ? "settled" : "open"} />}
            <Row k="Invoice" v={p.invoice ? `${p.invoice.invoice_number} · ${fmt(p.invoice.total_amount)} · ${p.invoice.payment_status}` : "not raised yet"} />
            {p.final && <Row k="Finalised" v={`${fmt(p.final.amount)} · ${p.final.weight} kg`} hint={`${p.final.by} · ${String(p.final.at || "").slice(0, 16).replace("T", " ")}${p.final.reason ? ` · ${p.final.reason}` : ""}`} />}
          </div>

          {!p.final_locked && (<>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="text-xs font-semibold text-slate-600">Final amount incl. GST (₹)
                <input type="number" step="0.01" className={inp + " mt-1"} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="final-amount" /></label>
              <label className="text-xs font-semibold text-slate-600">Final chargeable weight (kg)
                <input type="number" step="0.01" className={inp + " mt-1"} value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="final-weight" /></label>
            </div>
            {differs && <label className="text-xs font-semibold text-slate-600 block mb-3">Reason for the change (required)
              <input className={inp + " mt-1"} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Delhivery reconciled weight 150 kg" /></label>}
            <p className="text-[11px] text-slate-500 mb-3">Finalising locks amount and weight. Wallet customers are charged or refunded the difference; an unpaid invoice is re-priced under the same number.</p>
            <div className="flex gap-2">
              <button disabled={!ok || busy} onClick={() => finalise(false)} className="flex-1 px-3 py-2 rounded-lg border border-indigo-300 text-indigo-700 text-sm font-semibold hover:bg-indigo-50 disabled:opacity-50">Finalise</button>
              <button disabled={!ok || busy || !!p.invoice} onClick={() => finalise(true)} data-testid="finalise-and-invoice"
                className="flex-1 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Finalise & raise invoice"}</button>
            </div>
          </>)}
          {p.final_locked && (
            <div className="flex gap-2">
              {!p.invoice && <button disabled={busy} onClick={raise} className="flex-1 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">Raise invoice</button>}
              {isAdmin && <button disabled={busy} onClick={reopen} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">Reopen (admin)</button>}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}
